import React from 'react';

import Image from '3-components/Image/Image';
import Icon from '3-components/Icon/Icon';
import Button from '3-components/Buttons/Button';
import Chip from '3-components/Chip/Chip';
import { ShortDateFromTimestamp, RecentlySoldTimestampLimit } from '3-components/Utils/DateUtils';
import { IsRunningInFEDEnvironment } from '3-components/Utils/EnvironmentUtils';
import { Swiper, SwiperSlide } from 'swiper/react';
import { PropertyStatus } from '4-sections/PropertyForSaleListing/PropertyForSaleListing';

/* eslint-disable camelcase */
interface PropertySaleHitProps {
    objectID: string,
    name: string,
    type: string,
    intro: string,
    phone: string,
    street: string,
    suburb: string,
    state: string,
    postcode: string,
    village: string,
    '_geoloc': {
        lat: number,
        lng: number
    },
    sold_price?: string,
    sold_date_timestamp?: number;
    status?: string;
    image_gallery: [string],
    email: string,
    home_number: number,
    bedroom: string,
    bathroom: string,
    carspace: string,
    size: string,
    price: string,
    location_tag: [string],
    url: string,
    size_types: string,
    other_features: [string],
    home_for_sale?: boolean,
    '_rankingInfo'?: {
        geoDistance?: number
    }
}

interface PropertySaleCardProps {
    hit: PropertySaleHitProps;
}

interface PropertySaleCardCarouselProps {
    name: string;
    images: string[];
}

const PropertySaleCardCarousel = (props: PropertySaleCardCarouselProps) => {
    const { name, images } = props;

    if (!images) {
        return null;
    }

    return (
        <div className="swiper-container">
            <Swiper
                spaceBetween={0}
                slidesPerView={1}
                autoHeight={true}
                navigation={true}
            >
                {images.map((imageUrl, index) => {
                    const altText = `image ${index + 1} for ${name}`;
                    return (
                        <SwiperSlide
                            key={`${name}-${imageUrl}`}
                        >
                            <Image
                                imgSrc={imageUrl}
                                imgAlt={altText}
                                imgCustomClass="prsc-image-container"
                            />
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </div>
    );
};

const PropertySaleCard = (props: PropertySaleCardProps) => {
    const { hit } = props;

    const propertyIsSold = hit.status === PropertyStatus.Sold;
    const priceToUse = propertyIsSold && hit.sold_price ? hit.sold_price : hit.price;
    const displayPrice = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(parseInt(priceToUse, 10));
    const recentlySoldTimestampLimit = RecentlySoldTimestampLimit();

    const imageList = IsRunningInFEDEnvironment()
        ? [
            `https://picsum.photos/640/480?random=${hit.name}1`,
            `https://picsum.photos/640/480?random=${hit.name}2`,
            `https://picsum.photos/640/480?random=${hit.name}3`
        ]
        : hit.image_gallery;

    const allLocationTags = [...hit.location_tag];
    const MAX_TAG_COUNT = 3;
    const displayedLocationTags = allLocationTags.slice(0, MAX_TAG_COUNT);
    if (allLocationTags.length > MAX_TAG_COUNT) {
        displayedLocationTags.push(`+${allLocationTags.length - MAX_TAG_COUNT} more`);
    }

    return (
        <div className="property-sale-card">
            <div className="prsc-top-container">
                <PropertySaleCardCarousel
                    name={hit.name}
                    images={imageList}
                />
                <div className="prsc-content-wrapper">
                    <div className="prsc-location">
                        <Icon icon="map-pin" />
                        <span>{`${hit.village}, ${hit.suburb} ${hit.state}`}</span>
                    </div>
                    <h3 className="prsc-content-title">
                        <a className="prsc-name-link" href={hit.url}>
                            {hit.name}
                        </a>
                    </h3>
                    <div className="prsc-content-chip-container">
                        {displayedLocationTags && displayedLocationTags.map(tag => (<Chip chipTitle={tag} key={tag} />))}
                    </div>
                </div>
            </div>
            <div className="prsc-bottom-container">
                <div className="prsc-content-wrapper">
                    <ul className="prsc-info">
                        {hit.bedroom && (
                            <li className="prsc-info-item">
                                <Icon icon="bed" />
                                <span>{`${hit.bedroom} ${parseInt(hit.bedroom, 10) === 1 ? 'bedroom' : 'bedrooms'}`}</span>
                            </li>
                        )}
                        {hit.bathroom && (
                            <li className="prsc-info-item">
                                <Icon icon="bath" />
                                <span>{`${hit.bathroom} ${parseInt(hit.bathroom, 10) === 1 ? 'bathroom' : 'bathrooms'}`}</span>
                            </li>
                        )}
                        {hit.carspace && (
                            <li className="prsc-info-item">
                                <Icon icon="car" />
                                <span>{`${hit.carspace} car space`}</span>
                            </li>
                        )}
                        {hit.size && (
                            <li className="prsc-info-item">
                                <Icon icon="maximize-2" />
                                <span>{hit.size}m<sup>2</sup></span>
                            </li>
                        )}
                    </ul>
                    <div className="prsc-info-action">
                        <div className="prsc-price-start">
                            <span className="price-text">
                                {displayPrice}
                                {propertyIsSold && hit.sold_date_timestamp && hit.sold_date_timestamp > recentlySoldTimestampLimit && (
                                    <span className="price-text-info">Sold on {ShortDateFromTimestamp(hit.sold_date_timestamp)}</span>
                                )}
                            </span>
                        </div>
                        <Button
                            btnText="View details"
                            href={hit.url}
                            btnCustomClass="action-button"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
/* eslint-disable camelcase */
export default PropertySaleCard;
